import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import MeritCard from "@/components/MeritCard";
import SignupDialog from "@/components/SignupDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldAlert, ShieldCheck, Gift, UserCog, Info, Flame, ScrollText, Sparkles, MessageCircle } from "lucide-react";

const PACKAGE_INCLUDES = {
  zh: [
    { icon: ScrollText, title: "太岁上疏祈福", desc: "师父代你向当年太岁星君上疏文、禀明姓名生辰，祈求流年平安顺遂。" },
    { icon: Flame, title: "点太岁灯 · 诵经回向", desc: "为你点一盏太岁灯，诵经持咒并回向，化解冲刑之气。" },
    { icon: Sparkles, title: "开运锦囊指引", desc: "附上今年个人趋吉避凶的方位、颜色与注意事项指引。" },
    { icon: MessageCircle, title: "WhatsApp 跟进交付", desc: "付款确认后由师父人工安排，过程与结果透过 WhatsApp 通知你。" },
  ],
  en: [
    { icon: ScrollText, title: "Petition to Tai Sui", desc: "Our master petitions the year's Grand Duke with your name and birth details for a safe, smooth year." },
    { icon: Flame, title: "Tai Sui lamp & chanting", desc: "A Tai Sui lamp is lit, sutras chanted and merit dedicated to dissolve clash/punishment energy." },
    { icon: Sparkles, title: "Fortune-pouch guide", desc: "A personal guide to your lucky directions, colours and cautions for the year." },
    { icon: MessageCircle, title: "WhatsApp follow-up", desc: "Arranged personally after payment; the process and outcome are shared with you via WhatsApp." },
  ],
};

export default function TaiSuiView() {
  const { lang } = useI18n();
  const { settings } = useSettings();
  const [taisui, setTaisui] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | incomplete | error
  const [year, setYear] = useState(null);         // selected target year (null => backend resolves)
  const [detailOpen, setDetailOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [outlook, setOutlook] = useState(null);

  const thisYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 12 }, (_, i) => thisYear + i);

  useEffect(() => {
    setState("loading");
    setTaisui(null);
    const params = { lang };
    if (year) params.year = year;
    api.get("/chart/taisui", { params })
      .then((r) => {
        if (r.data?.profile_complete === false) { setState("incomplete"); }
        else if (r.data?.available) { setTaisui(r.data); setState("ready"); }
        else { setState("error"); }
      })
      .catch(() => setState("error"));
  }, [lang, year]);

  // 12-year outlook (loads once, independent of selected year)
  useEffect(() => {
    setOutlook(null);
    api.get("/chart/taisui-outlook", { params: { lang } })
      .then((r) => { if (r.data?.profile_complete !== false) setOutlook(r.data.years || []); })
      .catch(() => { /* non-blocking */ });
  }, [lang]);

  if (state === "loading") {
    return (
      <div className="space-y-4" data-testid="taisui-loading">
        <Skeleton className="h-8 w-64 rounded-lg" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (state === "incomplete") {
    return (
      <Card className="border-border" data-testid="taisui-incomplete">
        <CardContent className="p-8 text-center">
          <UserCog className="h-8 w-8 mx-auto mb-3 text-[hsl(var(--brand-bronze))]" />
          <p className="text-sm text-muted-foreground">
            {lang === "zh"
              ? "请先在「个人资料」完整填写出生年、月、日、时，才能进行犯太岁分析。"
              : "Please complete your birth year, month, day and hour in your profile to run the Tai Sui analysis."}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (state === "error" || !taisui) {
    return (
      <Card className="border-border" data-testid="taisui-error">
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          {lang === "zh" ? "暂时无法加载犯太岁分析，请稍后再试。" : "Unable to load the Tai Sui analysis. Please try again later."}
        </CardContent>
      </Card>
    );
  }

  // admin-editable ritual items (fallback to built-in defaults)
  const customIncludes = settings?.taisui?.includes;
  const includeIcons = [ScrollText, Flame, Sparkles, MessageCircle];
  const includes = (Array.isArray(customIncludes) && customIncludes.length > 0)
    ? customIncludes
        .map((it, i) => ({
          icon: includeIcons[i % includeIcons.length],
          title: lang === "zh" ? (it.title_zh || it.title_en) : (it.title_en || it.title_zh),
          desc: lang === "zh" ? (it.desc_zh || it.desc_en) : (it.desc_en || it.desc_zh),
        }))
        .filter((it) => it.title || it.desc)
    : PACKAGE_INCLUDES[lang === "zh" ? "zh" : "en"];

  const purchased = taisui.package_status === "approved";
  const purchasePending = taisui.package_status === "pending";

  return (
    <div className="space-y-4" data-testid="taisui-view">
      <Card className="border-border overflow-hidden" data-testid="taisui-card">
        <div className="h-1.5 w-full" style={{ background: taisui.offends ? "hsl(var(--brand-vermillion))" : "hsl(150 42% 36%)" }} />
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {taisui.offends
                ? <ShieldAlert className="h-5 w-5 text-[hsl(var(--brand-vermillion))]" />
                : <ShieldCheck className="h-5 w-5 text-[hsl(150_42%_36%)]" />}
              <h3 className="font-display text-xl font-semibold" data-testid="taisui-title">
                {lang === "zh" ? `${taisui.year_info.year} 犯太岁分析` : `${taisui.year_info.year} Tai Sui Analysis`}
              </h3>
              <Badge variant="outline" className="font-mono-num" data-testid="taisui-year-ganzhi">
                {taisui.year_info.ganzhi} · {lang === "zh" ? `${taisui.year_info.zodiac_zh}年` : taisui.year_info.zodiac_en}
              </Badge>
              {purchased && (
                <Badge className="bg-[hsl(150_42%_36%)] text-white hover:bg-[hsl(150_42%_36%)]" data-testid="taisui-purchased-badge">
                  {lang === "zh" ? "已请购化太岁" : "Tai Sui remedy purchased"}
                </Badge>
              )}
              {purchasePending && (
                <Badge className="bg-[hsl(38_70%_50%)] text-white hover:bg-[hsl(38_70%_50%)]" data-testid="taisui-pending-badge">
                  {lang === "zh" ? "化太岁 · 审核中" : "Tai Sui remedy · pending"}
                </Badge>
              )}
            </div>
            {/* 往年对照 year picker */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{lang === "zh" ? "选择年份" : "Year"}</span>
              <Select value={String(year || taisui.year_info.year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="h-8 w-[120px]" data-testid="taisui-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEARS.map((y) => <SelectItem key={y} value={String(y)} data-testid={`taisui-year-opt-${y}`}>{y}{lang === "zh" ? "年" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-5 mb-4">
            {lang === "zh"
              ? `很多人以为只有生肖年才犯太岁；其实从八字看，年、月、日、时四柱地支都可能与太岁（${taisui.taisui_zhi}）形成值/冲/刑/害/破。以下逐柱分析你 ${taisui.year_info.year} 年的犯太岁情况。`
              : `Many think only the birth-year zodiac offends Tai Sui. In BaZi, any of the four pillars' branches can relate to the year branch (${taisui.taisui_zhi}). Below is a pillar-by-pillar view for ${taisui.year_info.year}.`}
          </p>

          {/* severity */}
          <div className="rounded-xl border border-border bg-[hsl(var(--secondary))]/30 p-4 mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold">{lang === "zh" ? "犯太岁严重度" : "Severity"}</span>
              <span className="text-sm font-mono-num font-bold" data-testid="taisui-severity"
                style={{ color: taisui.offends ? "hsl(var(--brand-vermillion))" : "hsl(150 42% 36%)" }}>
                {taisui.severity}% · {taisui.severity_level}
              </span>
            </div>
            <Progress value={taisui.severity} className="h-2" data-testid="taisui-severity-bar" />
            <p className="text-[11px] text-muted-foreground mt-1.5">
              {taisui.offends
                ? (lang === "zh" ? `四柱中有 ${taisui.offending_pillars.length} 柱犯太岁，建议留意并适当化解。` : `${taisui.offending_pillars.length} of 4 pillars offend Tai Sui this year.`)
                : (lang === "zh" ? "四柱与太岁无直接冲突，流年相对平顺。" : "No direct conflict with Tai Sui — a relatively smooth year.")}
            </p>
          </div>

          {/* per-pillar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4" data-testid="taisui-pillars">
            {taisui.pillars.map((p) => (
              <div key={p.pillar}
                className={`rounded-xl border p-3 text-center ${p.offends ? "border-[hsl(var(--brand-vermillion))]/50 bg-[hsl(var(--brand-vermillion))]/5" : "border-border bg-card"}`}
                data-testid={`taisui-pillar-${p.pillar}`}>
                <div className="text-[11px] text-muted-foreground">{lang === "zh" ? p.pillar_zh : p.pillar_en}</div>
                <div className="font-mono-num text-lg font-bold my-0.5">{p.ganZhi}</div>
                <div className="text-[11px] text-muted-foreground mb-1.5">{lang === "zh" ? `${p.branch} · ${p.zodiac_zh}` : `${p.branch} · ${p.zodiac_en}`}</div>
                {p.offends ? (
                  <div className="flex flex-wrap gap-1 justify-center">
                    {p.type_labels.map((tl, i) => (
                      <Badge key={i} className="h-5 px-1.5 text-[10px] bg-[hsl(var(--brand-vermillion))] text-white hover:bg-[hsl(var(--brand-vermillion))]">{tl}</Badge>
                    ))}
                  </div>
                ) : (
                  <Badge variant="outline" className="h-5 px-1.5 text-[10px] text-[hsl(150_42%_36%)] border-[hsl(150_42%_36%)]/40">{lang === "zh" ? "无犯" : "Clear"}</Badge>
                )}
              </div>
            ))}
          </div>

          {/* reasons for offending pillars */}
          {taisui.offends && (
            <ul className="list-disc pl-5 space-y-1 text-sm text-foreground/85 mb-4" data-testid="taisui-reasons">
              {taisui.pillars.filter((p) => p.offends).map((p) => (
                <li key={p.pillar}>
                  <span className="font-semibold">{lang === "zh" ? p.pillar_zh : p.pillar_en}（{p.type_labels.join("、")}）：</span>
                  {p.reasons.join(" ")}
                </li>
              ))}
            </ul>
          )}

          {/* year-wide affected zodiacs */}
          <div className="rounded-xl bg-[hsl(var(--brand-gold-soft))]/30 p-3 mb-1">
            <div className="text-[11px] font-semibold text-[hsl(var(--brand-bronze))] mb-1.5">
              {lang === "zh" ? `${taisui.year_info.year} 年犯太岁生肖` : `Zodiacs offending Tai Sui in ${taisui.year_info.year}`}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {taisui.affected_zodiacs.map((z) => (
                <Badge key={z.branch} variant="outline" className="text-[11px]" data-testid={`taisui-zodiac-${z.branch}`}>
                  {lang === "zh" ? z.zodiac_zh : z.zodiac_en} · {z.type_labels.join("/")}
                </Badge>
              ))}
            </div>
          </div>

          {/* remedy package */}
          {taisui.offends && taisui.package && (
            <div className="mt-4" data-testid="taisui-package">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Gift className="h-4 w-4 text-[hsl(var(--brand-bronze))]" />
                  <span className="text-sm font-semibold">{lang === "zh" ? "为你推荐 · 化解配套" : "Recommended remedy"}</span>
                </div>
                <Button variant="outline" size="sm" className="h-8" onClick={() => setDetailOpen(true)} data-testid="taisui-package-detail-btn">
                  <Info className="h-3.5 w-3.5 mr-1" />{lang === "zh" ? "查看配套内容" : "What's included"}
                </Button>
              </div>
              <div className="max-w-sm"><MeritCard project={taisui.package} /></div>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-center text-[11px] text-muted-foreground">
        {lang === "zh" ? "命理解读仅供参考，运势掌握在自己手中。" : "For reference only — your destiny is in your own hands."}
      </p>

      {/* 配套内容 details dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="taisui-package-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Gift className="h-5 w-5 text-[hsl(var(--brand-bronze))]" />
              {taisui.package ? (lang === "zh" ? taisui.package.title_zh : (taisui.package.title_en || taisui.package.title_zh)) : ""}
            </DialogTitle>
            <DialogDescription>
              {lang === "zh" ? "付费前先了解这份化太岁祈福配套具体包含什么。" : "Know exactly what this Tai Sui blessing package includes before you pay."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {PACKAGE_INCLUDES && includes.map((it, i) => (
              <div key={i} className="flex gap-3 items-start" data-testid={`taisui-include-${i}`}>
                <div className="mt-0.5 rounded-full bg-[hsl(var(--brand-gold-soft))]/60 p-2">
                  <it.icon className="h-4 w-4 text-[hsl(var(--brand-bronze))]" />
                </div>
                <div>
                  <div className="text-sm font-semibold">{it.title}</div>
                  <div className="text-xs text-muted-foreground leading-5">{it.desc}</div>
                </div>
              </div>
            ))}
          </div>
          {taisui.package && (
            <div className="mt-2 flex items-center justify-between rounded-xl bg-[hsl(var(--secondary))]/40 p-3">
              <span className="text-sm text-muted-foreground">{lang === "zh" ? "随喜功德" : "Offering"}</span>
              <span className="font-mono-num text-lg font-bold text-[hsl(var(--brand-ink))]">RM{Number(taisui.package.price || 0).toFixed(2)}</span>
            </div>
          )}
          <Button
            className="w-full bg-[hsl(var(--primary))] text-primary-foreground"
            onClick={() => setSignupOpen(true)}
            data-testid="taisui-detail-signup-btn">
            <Gift className="h-4 w-4 mr-1.5" />{lang === "zh" ? "立即报名参与" : "Register now"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* one-click register straight from the details popup (stacks over it) */}
      {taisui.package && signupOpen && (
        <SignupDialog project={taisui.package} open={signupOpen} onOpenChange={setSignupOpen} />
      )}

      {/* 全年对照表 / 12-year outlook */}
      {outlook && outlook.length > 0 && (
        <Card className="border-border" data-testid="taisui-outlook">
          <CardContent className="p-5 sm:p-6">
            <h3 className="font-display text-lg font-semibold mb-1">{lang === "zh" ? "未来十二年犯太岁对照表" : "12-Year Tai Sui Outlook"}</h3>
            <p className="text-xs text-muted-foreground mb-3">{lang === "zh" ? "一览未来各流年犯太岁情况，提早留意需化解的年份。" : "See which upcoming years to watch at a glance."}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="taisui-outlook-table">
                <thead>
                  <tr className="text-left text-[11px] text-muted-foreground border-b border-border">
                    <th className="py-2 pr-2 font-medium">{lang === "zh" ? "年份" : "Year"}</th>
                    <th className="py-2 pr-2 font-medium">{lang === "zh" ? "干支/生肖" : "Pillar"}</th>
                    <th className="py-2 pr-2 font-medium">{lang === "zh" ? "犯太岁" : "Tai Sui"}</th>
                    <th className="py-2 pr-2 font-medium text-right">{lang === "zh" ? "严重度" : "Severity"}</th>
                  </tr>
                </thead>
                <tbody>
                  {outlook.map((y) => (
                    <tr key={y.year} className={`border-b border-border/60 ${y.offends ? "bg-[hsl(var(--brand-vermillion))]/5" : ""}`} data-testid={`taisui-outlook-row-${y.year}`}>
                      <td className="py-2 pr-2 font-mono-num font-semibold">{y.year}</td>
                      <td className="py-2 pr-2 text-muted-foreground">{y.ganzhi} · {lang === "zh" ? y.zodiac_zh : y.zodiac_en}</td>
                      <td className="py-2 pr-2">
                        {y.offends ? (
                          <div className="flex flex-wrap gap-1">
                            {y.offending_pillars.map((p, i) => (
                              <Badge key={i} className="h-5 px-1.5 text-[10px] bg-[hsl(var(--brand-vermillion))] text-white hover:bg-[hsl(var(--brand-vermillion))]">
                                {(lang === "zh" ? p.pillar_zh : p.pillar_en)}·{p.type_labels.join("/")}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[hsl(150_42%_36%)] text-xs">{lang === "zh" ? "平顺" : "Clear"}</span>
                        )}
                      </td>
                      <td className="py-2 pr-0 text-right font-mono-num font-semibold" style={{ color: y.offends ? "hsl(var(--brand-vermillion))" : "hsl(150 42% 36%)" }}>
                        {y.severity}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
